/**
 * One in-flight call per key, however many callers ask for it.
 *
 * Two unrelated atoms wanting the same thing at the same moment is normal in
 * this app: a screen and the overlay above it both resolve the same pair, a
 * list and a detail panel both resolve the same wallet. A cache answers
 * "already loaded"; nothing answers "loading right now", so on a cold cache
 * every one of them goes to the network and the slowest reply sets the pace.
 *
 * Measured on staging opening a pair from the feed: `pair-info` went out at
 * 739ms and again at 740ms, and the second took 2,966ms — the slowest request
 * on the page was a duplicate of the fastest.
 *
 * The entry is dropped the moment the call settles, so this is not a cache and
 * holds nothing: it only collapses the overlap. Whatever caching the caller
 * wants is still the caller's own.
 *
 * The shared call must not be tied to one caller's cancellation — the caller
 * that unmounts would take the other's answer with it. Pass a request that
 * does not carry a per-caller abort signal (for `apiRequest`, that is
 * `abort: 'none'`). Each caller's own state still follows its own abort,
 * because its `ctx.schedule` throws regardless of what the request does.
 */
export const createCoalescer = <A>() => {
  const inFlight = new Map<string, Promise<A>>()

  return {
    /**
     * `start` is called only when nothing is already running for `key`;
     * otherwise the running call's promise is returned as it is, so every
     * caller settles together on the same answer — including a rejection.
     */
    run: (key: string, start: () => Promise<A>): Promise<A> => {
      const running = inFlight.get(key)
      if (running) {
        return running
      }

      const started = start().finally(() => {
        inFlight.delete(key)
      })
      inFlight.set(key, started)

      return started
    },

    /** In-flight calls right now. For tests and for debugging. */
    get pending() {
      return inFlight.size
    },
  }
}

export type Coalescer<A> = ReturnType<typeof createCoalescer<A>>
